import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useContentItemViewNode } from 'components/WorkspaceEditor/hooks'
import { useNotification } from 'components/WorkspaceEditor/services'
import { NotificationContextProps } from 'components/WorkspaceEditor/services/notificationService'
import { useCallback } from 'react'
import type {
    EditorRef,
    FocusManager,
    NotificationHandler,
    UseContentValidationReturn,
    ValidationContext,
} from '../types'
import { articleDuplicateValidator } from '../utils/articleDuplicateValidator'

/**
 * Creates notification handler for error messages
 */
const notificationHandler = (
    notification: NotificationContextProps
): NotificationHandler => ({
    showError: (message: string, duration = 5000) => {
        notification.error({ message, duration })
    },
})

/**
 * Creates editor focus manager with delay
 */
const focusManager = (): FocusManager => ({
    focusEditor: (editorRef?: EditorRef) => {
        setTimeout(() => {
            if (editorRef?.current?.focus) {
                editorRef.current.focus()
            }
        }, 100)
    },
})

/**
 * Hook for validating duplicate article names with automatic error handling
 * @returns {UseContentValidationReturn} Validation methods for add/update operations
 */
export const useDuplicateArticleValidation = (): UseContentValidationReturn => {
    const notification = useNotification()
    const { nodeToAdd, contentItemViewNodes } = useContentItemViewNode()

    /**
     * Validates text and handles errors/notifications
     */
    const validateAndHandle = useCallback(
        async (context: ValidationContext): Promise<boolean> => {
            const { isAddOperation, nodeType, editorRef } = context

            // Check if validating an article
            const isArticle = isAddOperation
                ? nodeToAdd?.type === 'Article'
                : nodeType === 'Article'

            // Only validate articles
            if (!isArticle) return true

            // Ensure nodes exist
            if (!contentItemViewNodes?.length) return true

            // Build validation context
            const ctx: ValidationContext = {
                ...context,
                contentNodes: contentItemViewNodes,
            }

            // Create handlers
            const notify = notificationHandler(notification)
            const focus = focusManager()
            const validator = articleDuplicateValidator()

            try {
                const result = validator.validate(ctx)

                if (!result.isValid && result.errorMessage) {
                    notify.showError(result.errorMessage)
                    focus.focusEditor(editorRef)
                    return false
                }
            } catch (error) {
                // If validation function fails, log the error and allow the operation to proceed
                console.warn('Article duplicate validation error:', error)
                return true
            }

            return true
        },
        [contentItemViewNodes, nodeToAdd, notification]
    )

    /**
     * Validates new article for duplicates
     */
    const validateAdd = useCallback(
        (
            text: string,
            textElement: TextContentElement,
            editorRef?: EditorRef
        ) => {
            return validateAndHandle({
                text,
                textElement,
                isAddOperation: true,
                editorRef,
                contentNodes: contentItemViewNodes || [],
            })
        },
        [validateAndHandle, contentItemViewNodes]
    )

    /**
     * Finds node by element ID
     */
    const findNodeByElement = useCallback(
        (textElement: TextContentElement) => {
            if (!contentItemViewNodes?.length) return undefined

            return contentItemViewNodes.find((node) =>
                node?.elements?.some((element) => element.id === textElement.id)
            )
        },
        [contentItemViewNodes]
    )

    /**
     * Validates article update for duplicates
     */
    const validateUpdate = useCallback(
        (
            text: string,
            textElement: TextContentElement,
            editorRef?: EditorRef
        ) => {
            const currentNode = findNodeByElement(textElement)

            return validateAndHandle({
                text,
                textElement,
                isAddOperation: false,
                nodeType: currentNode?.textType || '',
                editorRef,
                contentNodes: contentItemViewNodes || [],
            })
        },
        [validateAndHandle, findNodeByElement, contentItemViewNodes]
    )

    return {
        validateAdd,
        validateUpdate,
        validateAndHandle,
    }
}
